import { useEffect, useRef } from 'react';

/**
 * The overlay-history law, implemented locally.
 *
 * On a phone the platform back gesture is the primary way people dismiss
 * things. An overlay that owns the screen — a sheet, a full-screen picker, a
 * multi-step flow — must therefore join the history stack, so that back closes
 * IT and not the route underneath it. Without this, a receptionist with a
 * half-typed invoice open swipes back and loses the whole screen.
 *
 * `@parsina/framework` grows a `useOverlayHistory` of its own at v0.11+, and
 * `BottomSheet` calls it internally from then on. This app is pinned at
 * v0.10.1, so the behaviour lives here — as a thin module with the same
 * contract, so that raising the pin is a delete-and-re-export rather than a
 * rewrite. See docs/MOBILE-REDESIGN.md §4.2.
 *
 * ── The depth counter ──
 *
 * `depth` is module-level rather than per-hook because the question it answers
 * is global: "does any overlay currently own a history entry?". A route-level
 * leave guard is a different component from the sheet, and it has no other way
 * to tell a genuine back-out-of-the-route from a back-that-closed-a-sheet.
 *
 * `justPopped` covers the other half of the same problem. When an overlay is
 * closed from inside (the × button, a successful submit) it pops its own
 * entry, and that pop reaches any `popstate` listener on the page a tick
 * later. A guard that treats it as a navigation asks "discard your changes?"
 * every single time a sheet is dismissed — which is the exact bug this flag
 * exists to prevent.
 *
 * ── The hand-off ──
 *
 * A step flow swaps one sheet for the next in a SINGLE React commit: picking
 * the doctor in «ثبت نوبت» unmounts the picker and mounts `BookingModal` in the
 * same flush. React runs every destroy before every create, so a release that
 * called `history.back()` there and then would be immediately followed by the
 * next sheet's `pushState` — and `back()` is QUEUED, not synchronous. The
 * traversal resolves after the push, so the entry it takes off the stack is
 * the one the NEW sheet just added, and the `popstate` lands on the new
 * sheet's own listener, which reads it as the platform back and closes the
 * flow. The operator picks a doctor and the whole booking disappears.
 *
 * jsdom cannot show this — it drops a traversal that a `pushState` overtakes,
 * so the hook's own tests passed the entire time the phone was broken. Chrome
 * and WebKit both fire the pop.
 *
 * So a release is DEFERRED to a microtask, which is after React's whole
 * destroy-then-create flush, and an overlay mounting inside that same flush
 * CLAIMS the pending entry instead of pushing one of its own. Two sheets, one
 * entry, no traversal at all: back still closes the flow exactly once, and the
 * step swap never touches the history stack.
 *
 * ── The repurposed entry ──
 *
 * The same hazard has a third face, and it is the one that needs no cooperation
 * from the call site. A sheet that writes to the URL while it is open — every
 * filter sheet on the phone, and «بیماران»'s row menu, which opens the record
 * by putting `patient=` in the query — does it with a REPLACE, and the entry it
 * replaces is the overlay's own, because the overlay pushed the entry that is
 * on top. Popping that on close would undo the write: the filters snapped back
 * the moment the sheet closed, and the record drawer never opened at all.
 *
 * A `replace` writes its own state object, so the mark this hook pushed is no
 * longer there — which is exactly the signal. An entry whose mark has gone has
 * been repurposed by the page, and is no longer the overlay's to pop. One rule,
 * no call site has to remember anything, and `consumeOverlayEntry` stays for
 * the case the mark cannot see: a PUSH, where the destination is a new entry
 * stacked ABOVE the overlay's rather than written into it.
 *
 * The mark is read off the stack top at cleanup, which is the overlay's own
 * entry for the overlay being dismissed. Two sheets nested one inside the other
 * where the INNER one is the one replaced would read the inner entry for both;
 * no screen does that today, and the cost would be one entry left behind rather
 * than a lost navigation.
 */

let depth = 0;
let justPopped = false;
// Entries promised to a navigation that is about to replace them. See
// `consumeOverlayEntry`.
let consumed = 0;
let justPoppedTimer = null;
// Entries whose overlay has gone but which are still on the stack, waiting for
// either the next sheet to claim one or the microtask below to pop them.
let pendingRelease = 0;
let releaseScheduled = false;

const MARK = '__parsinaOverlay';

/** How many overlays currently hold a history entry. */
export function overlayHistoryDepth() {
  return depth;
}

/**
 * Does an overlay own the pop that is being processed right now?
 *
 * A route-level leave guard's `popstate` listener MUST ask this first and
 * return early when it is true. True means either an overlay still holds an
 * entry (the platform back is in the middle of closing it) or one has just
 * popped its own entry after an in-app close.
 */
export function overlayOwnsPop() {
  // `pendingRelease` is the deferred half: the overlay has gone but its entry
  // is still on the stack and a pop for it is on the way.
  return depth > 0 || pendingRelease > 0 || justPopped;
}

/**
 * Pop every entry no overlay owns any more.
 *
 * Deferred to a microtask so that a step flow's next sheet — mounted in the
 * same React flush as the previous one's teardown — gets to claim the entry
 * first. One `go(-n)` rather than n `back()` calls: each queued traversal
 * resolves against the stack as it is when it RUNS, so issuing them separately
 * is the same race in miniature.
 */
function flushRelease() {
  releaseScheduled = false;
  const n = pendingRelease;
  pendingRelease = 0;
  if (n <= 0 || typeof window === 'undefined') return;
  markPopped();
  window.history.go(-n);
}

function releaseEntry() {
  pendingRelease += 1;
  if (releaseScheduled) return;
  releaseScheduled = true;
  queueMicrotask(flushRelease);
}

function markPopped() {
  justPopped = true;
  if (justPoppedTimer) clearTimeout(justPoppedTimer);
  // One task is enough: `popstate` listeners registered elsewhere run in the
  // same turn as the pop. The timer only guards against a listener that
  // defers its own work.
  justPoppedTimer = setTimeout(() => { justPopped = false; justPoppedTimer = null; }, 0);
}

/**
 * Give `open`/`onClose` a history entry.
 *
 * @param open      whether the overlay is currently shown
 * @param onClose   called when the platform back dismisses it
 * @param enabled   pass false for a desktop dialog, where back has always
 *                  meant "leave the page"
 */
export default function useOverlayHistory(open, onClose, enabled = true) {
  const ownsEntry = useRef(false);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!enabled || !open || typeof window === 'undefined') return undefined;

    if (pendingRelease > 0) {
      // A sheet that closed in this same flush left a marked entry behind and
      // we are its replacement: take it over. Pushing here instead is what
      // made picking a doctor close the booking flow — see the hand-off note.
      pendingRelease -= 1;
    } else {
      // Push our own entry. The state mark lets a listener recognise it; the
      // URL is deliberately unchanged, because the overlay is not a route.
      window.history.pushState({ ...(window.history.state || {}), [MARK]: true }, '');
    }
    ownsEntry.current = true;
    depth += 1;

    const onPop = () => {
      if (!ownsEntry.current) return;
      // The browser has already removed our entry by the time this fires.
      ownsEntry.current = false;
      depth = Math.max(0, depth - 1);
      markPopped();
      closeRef.current?.();
    };

    window.addEventListener('popstate', onPop);

    return () => {
      window.removeEventListener('popstate', onPop);
      // Closed from inside rather than by the back gesture: take our entry
      // back off the stack, or every sheet the operator opens leaves one
      // behind and back stops working entirely.
      if (ownsEntry.current) {
        ownsEntry.current = false;
        depth = Math.max(0, depth - 1);
        // Promised to a navigation that is replacing this entry: leave it on
        // the stack for the replace to overwrite, or the traversal would eat
        // the destination instead (see `consumeOverlayEntry`).
        if (consumed > 0) consumed -= 1;
        // Already overwritten by one. A `replace` writes its own state object,
        // so the mark we pushed is gone — the slot now belongs to the page,
        // and popping it would undo the navigation. See the repurposing note.
        else if (!window.history.state?.[MARK]) { /* not ours any more */ }
        else releaseEntry();
      }
    };
  }, [enabled, open]);
}

/**
 * Hand this overlay's entry to a navigation that is replacing it.
 *
 * Closing a sheet in order to GO somewhere is not the same as dismissing it,
 * and treating the two alike is what made every «بیشتر» destination a no-op:
 *
 *   1. `go()` calls `onClose()` and then navigates.
 *   2. The router's `pushState` lands FIRST — it is synchronous — so the
 *      destination is now the top of the stack, sitting above the overlay's
 *      own entry.
 *   3. The sheet unmounts, its cleanup calls `releaseEntry()`, and the
 *      deferred `history.go(-1)` takes off the top entry — which is no longer
 *      the overlay's, it is the page the operator just asked for.
 *
 * The result was a menu whose every row closed itself and stayed put. The trap
 * is the same "a traversal resolves against the stack as it is when it RUNS"
 * hazard the hand-off note above describes, reached from the other direction:
 * there a push overtook a pop, here a pop lands on a push.
 *
 * So a caller that is navigating says so BEFORE closing. The cleanup then skips
 * the release and leaves the entry on the stack, and the caller navigates with
 * `replace: true` so the destination takes the overlay's slot rather than
 * stacking above it — which keeps the depth right and makes one back press
 * return to the page the sheet was opened from.
 *
 * **Returns whether there was an entry to hand over**, which is the `replace`
 * the caller should then navigate with. That is not a convenience: the same
 * handler runs on both surfaces. An `AdaptiveModal` is a sheet on a phone and a
 * centred `Dialog` on the desk, and only the sheet holds a history entry — so
 * a desktop dialog must still PUSH its destination, or back would swallow the
 * page the operator opened the dialog from. A row menu's handler is called from
 * the menu sheet AND from the card's own tap for the same reason.
 *
 * Nothing is consumed when no overlay holds an entry, so the call is safe
 * anywhere. Call it only when the overlay is closing in the same turn: a
 * consume that no cleanup collects would be spent by the NEXT sheet to close,
 * which would then leak its entry.
 */
export function consumeOverlayEntry() {
  if (depth <= 0) return false;
  consumed += 1;
  return true;
}

/** Test seam: reset the module state between cases. */
export function __resetOverlayHistory() {
  depth = 0;
  justPopped = false;
  consumed = 0;
  pendingRelease = 0;
  releaseScheduled = false;
  if (justPoppedTimer) { clearTimeout(justPoppedTimer); justPoppedTimer = null; }
}
